import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from 'motion/react';
import { useUiStore, type Toast } from '@/stores/ui';
import { useMotionPrefs } from '@/lib/use-motion-prefs';
import { cn } from '@/lib/cn';

const TONES: Record<Toast['tone'], string> = {
  neutral: 'bg-surface text-text border-line',
  success: 'bg-success-bg text-success-ink border-success',
  danger: 'bg-danger-bg text-danger-ink border-danger',
};

function ToastRow({ toast }: { toast: Toast }) {
  const dismiss = useUiStore((s) => s.dismissToast);

  useEffect(() => {
    const id = setTimeout(() => dismiss(toast.id), toast.durationMs);
    return () => clearTimeout(id);
  }, [toast.id, toast.durationMs, dismiss]);

  return (
    <m.div
      layout
      role="status"
      className={cn(
        'pointer-events-auto rounded-lg border-2 px-4 py-3 font-semibold shadow-soft',
        TONES[toast.tone],
      )}
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
    >
      {toast.message}
    </m.div>
  );
}

/** Single mount point for transient messages, rendered above everything. */
export function ToastHost() {
  const toasts = useUiStore((s) => s.toasts);
  const { reduced } = useMotionPrefs();

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-[calc(env(safe-area-inset-top,0px)+8px)] z-[60] mx-auto flex max-w-[var(--content-max)] flex-col gap-2 px-4"
    >
      <AnimatePresence initial={!reduced}>
        {toasts.map((toast) => (
          <ToastRow key={toast.id} toast={toast} />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
